import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { ApiAuthError, apiFetch, apiLogin } from "../api";
import { useI18n } from "../i18n/I18nContext";
import {
  buildMasseCondPliMapFromBancAndStack,
  calcHumiditeCondPli,
  calcHumiditeCondStack,
  calcHumiditeImpPli,
  calcHumiditeImpStack,
  calcGainMassiqueImpregPli,
  formatHumiditePct,
} from "../utils/humiditeCalc";

const IMP_DRAFT_KEY = "woodoo_impregnation_draft_v1";

function impFetch(url, options = {}) {
  return apiFetch(url, { skipAuthRedirect: true, ...options });
}

function impDraftHasContent(draft) {
  if (!draft || typeof draft !== "object") return false;
  if (draft.melangePn || draft.temperature || draft.dureeTraitement) return true;
  if (Array.isArray(draft.selectedStacks) && draft.selectedStacks.length > 0) {
    return true;
  }
  const rows = draft.rows;
  if (!Array.isArray(rows)) return false;
  return rows.some(
    (r) =>
      (r?.plyPn && String(r.plyPn).trim()) ||
      toFloat(r?.masseConditionnement) > 0 ||
      (Array.isArray(r?.masses) && r.masses.some((m) => toFloat(m) > 0))
  );
}

function readImpDraft() {
  try {
    const raw = localStorage.getItem(IMP_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeImpDraft(payload) {
  try {
    localStorage.setItem(
      IMP_DRAFT_KEY,
      JSON.stringify({ ...payload, savedAt: new Date().toISOString() })
    );
  } catch (e) {
    console.warn("Brouillon imprégnation non sauvegardé", e);
  }
}

function clearImpDraft() {
  localStorage.removeItem(IMP_DRAFT_KEY);
}

function clampInt(v, min, max) {
  const n = parseInt(String(v || ""), 10);
  return Number.isNaN(n) ? min : Math.max(min, Math.min(max, n));
}

function makeRows(n) {
  return Array.from({ length: n }, () => ({
    plyPn: "",
    masseConditionnement: "",
    masseAnhydre: "",
    masses: [""],
    commentaire: "",
  }));
}

/** Largeur fixe colonne « Identifiant du pli » (ne doit pas rétrécir avec Masse 2+) */
const IMP_PLY_ID_COL_PX = 420;

const GAIN_MIN = 70;
const GAIN_MAX = 90;
/** Seuil pour forcer une nouvelle pesée (comme avant, mais < 69 % au lieu de < 70 %) */
const GAIN_RETRY_BELOW = 69;

function ensureMassesArray(masses) {
  if (!Array.isArray(masses) || masses.length === 0) return [""];
  return masses;
}

function isGainTooLow(gainStr) {
  const g = parseFloat(gainStr);
  return gainStr !== "" && !Number.isNaN(g) && g < GAIN_MIN;
}

function isGainTooHigh(gainStr) {
  const g = parseFloat(gainStr);
  return gainStr !== "" && !Number.isNaN(g) && g > GAIN_MAX;
}

function isGainNeedsRetry(gainStr) {
  const g = parseFloat(gainStr);
  return gainStr !== "" && !Number.isNaN(g) && g < GAIN_RETRY_BELOW;
}

/** Zone cible affichée en vert */
function isGainInRange(gainStr) {
  const g = parseFloat(gainStr);
  return gainStr !== "" && !Number.isNaN(g) && g >= GAIN_MIN && g <= GAIN_MAX;
}

/** Si le gain de la dernière pesée est < 69 %, propose une case Masse suivante */
function massSlotsToShow(row) {
  const masses = ensureMassesArray(row.masses);
  let slots = 1;
  for (let i = 0; i < masses.length; i++) {
    if (toFloat(masses[i]) <= 0) break;
    slots = i + 1;
    const gain = computeGainMassique(masses[i], row.masseAnhydre);
    if (isGainNeedsRetry(gain)) {
      slots = i + 2;
    } else {
      break;
    }
  }
  return Math.max(1, slots);
}

function resolveFinalMasse(row) {
  const masses = ensureMassesArray(row.masses);
  let lastAny = "";
  for (const m of masses) {
    if (toFloat(m) > 0) lastAny = m;
  }
  return lastAny;
}

function gainBadgeClass(gainStr) {
  if (gainStr == null || gainStr === "") return "imp-gain-neutral";
  if (isGainInRange(gainStr)) return "imp-gain-ok";
  if (isGainTooLow(gainStr) || isGainTooHigh(gainStr)) return "imp-gain-alert";
  return "imp-gain-neutral";
}

function toFloat(v) {
  if (!v && v !== 0) return 0;
  return Number(String(v).replace(",", ".")) || 0;
}

function normalizePn(v) {
  return String(v || "").replace(/\s+/g, "").replace(/-/g, "").trim();
}

function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function computeGainMassique(masseImp, masseAnhydre) {
  const g = calcGainMassiqueImpregPli(masseImp, masseAnhydre);
  if (g != null && Number.isFinite(g)) return g.toFixed(2);
  return "";
}

function resolveHCondPliForRow(row) {
  const direct = toFloat(row?.hCondPli);
  if (Number.isFinite(direct)) return direct;

  const hm = toFloat(row?.hMerlinPli);
  const hStack = toFloat(row?.hCondStackRef);
  const hBanc = toFloat(row?.hBancMoyenneRef);
  if (Number.isFinite(hm) && Number.isFinite(hStack) && hBanc > 0) {
    const h = calcHumiditeCondPli(hm, hStack, hBanc);
    if (h != null && Number.isFinite(h)) return h;
  }

  const mc = toFloat(row?.masseConditionnement);
  const mb = toFloat(row?.masseInitialeBanc);
  if (mc > 0 && mb > 0 && Number.isFinite(hm)) {
    const h = calcHumiditeCondStack(mc, mb, hm);
    if (h != null && Number.isFinite(h)) return h;
  }
  return null;
}

function computeHumiditeApresImpregnation(
  masseImp,
  masseConditionnement,
  masseAnhydre,
  row
) {
  const hCondPli = resolveHCondPliForRow(row);
  const h = calcHumiditeImpPli(
    masseImp,
    masseConditionnement,
    masseAnhydre,
    hCondPli
  );
  const fmt = formatHumiditePct(h, 2);
  return fmt ?? "";
}

export default function Impregnation() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [masseTotaleImpregnationManuelle, setMasseTotaleImpregnationManuelle] =
    useState("");
  const [useMasseTotaleManuelle, setUseMasseTotaleManuelle] = useState(false);

  const [operator, setOperator] = useState("");
  const [now, setNow] = useState(new Date());

  const [melangePn, setMelangePn] = useState("");
  const [melangeSearch, setMelangeSearch] = useState("");
  const [melangePns, setMelangePns] = useState([]);

  const [temperature, setTemperature] = useState("");
  const [dureeTraitement, setDureeTraitement] = useState("");

  const [totalPlis, setTotalPlis] = useState(1);

  const [pliesPn, setPliesPn] = useState("");
  const [pliesSearch, setPliesSearch] = useState("");
  const [pliesPns, setPliesPns] = useState([]);
  const [pliesMeta, setPliesMeta] = useState({});
  const [selectedStacks, setSelectedStacks] = useState([]);

  const [rows, setRows] = useState(() => makeRows(12));

  const melangeBoxRef = useRef(null);
  const pliesBoxRef = useRef(null);

  const [showMelangeList, setShowMelangeList] = useState(false);
  const [showPliesList, setShowPliesList] = useState(false);
  const [showMelangeScanner, setShowMelangeScanner] = useState(false);
  const [showPliesScanner, setShowPliesScanner] = useState(false);

  const [sending, setSending] = useState(false);
  const [loadingMasses, setLoadingMasses] = useState(false);
  const [loadingAnhydre, setLoadingAnhydre] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showReauth, setShowReauth] = useState(false);
  const [reauthUsername, setReauthUsername] = useState("");
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthBusy, setReauthBusy] = useState(false);
  const [reauthError, setReauthError] = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);

  const draftHydratedRef = useRef(false);

  useEffect(() => {
    if (!showReauth || reauthUsername) return;
    if (operator) {
      setReauthUsername(operator);
      return;
    }
    try {
      const raw = localStorage.getItem("user");
      if (raw) setReauthUsername(JSON.parse(raw).username || "");
    } catch {
      /* ignore */
    }
  }, [showReauth, operator, reauthUsername]);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) {
      try {
        setOperator(JSON.parse(raw).username || "");
      } catch (e) {
        console.error(e);
      }
    }

    if (draftHydratedRef.current) return;
    draftHydratedRef.current = true;
    const draft = readImpDraft();
    if (!impDraftHasContent(draft)) return;

    if (draft.melangePn) {
      setMelangePn(draft.melangePn);
      setMelangeSearch(draft.melangeSearch || draft.melangePn);
    }
    if (draft.temperature != null) setTemperature(String(draft.temperature));
    if (draft.dureeTraitement != null) {
      setDureeTraitement(String(draft.dureeTraitement));
    }
    if (Array.isArray(draft.selectedStacks) && draft.selectedStacks.length) {
      setSelectedStacks(draft.selectedStacks);
    }
    if (Array.isArray(draft.rows) && draft.rows.length) {
      setRows(draft.rows.map((r) => ({ ...makeRows(1)[0], ...r })));
    }
    if (typeof draft.totalPlis === "number") setTotalPlis(draft.totalPlis);
    if (draft.useMasseTotaleManuelle) setUseMasseTotaleManuelle(true);
    if (draft.masseTotaleImpregnationManuelle != null) {
      setMasseTotaleImpregnationManuelle(
        String(draft.masseTotaleImpregnationManuelle)
      );
    }
    const when = draft.savedAt
      ? new Date(draft.savedAt).toLocaleString("fr-FR")
      : "";
    setSuccess(
      when
        ? `Brouillon restauré (${when}). Vérifiez la saisie avant d'enregistrer.`
        : "Brouillon restauré. Vérifiez la saisie avant d'enregistrer."
    );
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!impDraftHasContent({
        melangePn,
        temperature,
        dureeTraitement,
        selectedStacks,
        rows,
      })) {
        return;
      }
      writeImpDraft({
        melangePn,
        melangeSearch,
        temperature,
        dureeTraitement,
        totalPlis,
        selectedStacks,
        rows,
        useMasseTotaleManuelle,
        masseTotaleImpregnationManuelle,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    melangePn,
    melangeSearch,
    temperature,
    dureeTraitement,
    totalPlis,
    selectedStacks,
    rows,
    useMasseTotaleManuelle,
    masseTotaleImpregnationManuelle,
  ]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    async function loadMelange() {
      try {
        const res = await impFetch("/api/solution_amorceur/list");
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== "success") return;
        const items = Array.isArray(data.items) ? data.items : [];
        setMelangePns(
          Array.from(new Set(items.map((i) => i.PN || i.pn).filter(Boolean)))
        );
      } catch (e) {
        console.error(e);
      }
    }
    loadMelange();
  }, []);

  async function reloadPlies() {
    try {
      const res = await impFetch("/api/empilement/list/impregnation");
      if (!res.ok) return;

      const data = await res.json().catch(() => ({}));
      const raw = Array.isArray(data)
        ? data
        : Array.isArray(data.items)
        ? data.items
        : [];

      const meta = {};
      const allIds = [];

      for (const it of raw) {
        if (typeof it === "string") {
          const sid = it.trim();
          if (!sid) continue;
          allIds.push(sid);
          meta[sid] = meta[sid] || 1;
        } else {
          const sid = String(
            it.StackId || it.stackId || it.StackID || ""
          ).trim();
          if (!sid) continue;
          const tp = Number(it.TotalPlis ?? it.totalPlis ?? 1) || 1;
          allIds.push(sid);
          meta[sid] = tp;
        }
      }

      setPliesMeta(meta);
      setPliesPns(Array.from(new Set(allIds)));
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    reloadPlies();
  }, []);

  useEffect(() => {
    const sum = selectedStacks.reduce(
      (acc, s) => acc + (Number(s.totalPlis) || 0),
      0
    );
    const n = clampInt(sum || 1, 1, 30);
    setTotalPlis(n);

    setRows((prev) => {
      const next = makeRows(n);
      for (let i = 0; i < Math.min(prev.length, next.length); i++) {
        next[i] = { ...next[i], ...prev[i] };
      }
      return next;
    });
  }, [selectedStacks]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (melangeBoxRef.current && !melangeBoxRef.current.contains(e.target)) {
        setShowMelangeList(false);
      }
      if (pliesBoxRef.current && !pliesBoxRef.current.contains(e.target)) {
        setShowPliesList(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const filteredMelange = useMemo(() => {
    const s = normalize(melangeSearch);
    return melangePns.filter((p) => !s || normalize(p).includes(s));
  }, [melangeSearch, melangePns]);

  const filteredPlies = useMemo(() => {
    const s = normalize(pliesSearch);
    return pliesPns.filter((p) => !s || normalize(p).includes(s));
  }, [pliesSearch, pliesPns]);

  async function fetchMassesForPlys(plyList, stackId = "") {
    const cleanList = Array.from(
      new Set((plyList || []).map(normalizePn).filter(Boolean))
    );
    if (!stackId && !cleanList.length) return {};

    try {
      setLoadingMasses(true);
      const url = stackId
        ? `/api/traitement-thermique/masse-par-pli?stackId=${encodeURIComponent(stackId)}`
        : `/api/traitement-thermique/masse-par-pli?plyPns=${encodeURIComponent(cleanList.join(","))}`;
      const res = await impFetch(url);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.status !== "success" || !Array.isArray(data.items)) {
        return {};
      }

      const out = {};
      for (const it of data.items) {
        const key = normalizePn(it.plyPn || it.PlyPN || it.Lotwoodoo);
        if (!key) continue;
        out[key] = toFloat(
          it.masseConditionnement ?? it.masse ?? it.Masse ?? 0
        );
      }
      return out;
    } catch (e) {
      console.error("Erreur fetch masses conditionnement :", e);
      return {};
    } finally {
      setLoadingMasses(false);
    }
  }

  async function fetchMasseAnhydreForStacks(stackId) {
  if (!stackId) return {};

  try {
    setLoadingMasses(true);

    const query = encodeURIComponent(stackId);
    const res = await impFetch(`/api/traitement-thermique/masse-anhydre?stackIds=${query}`);
    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.status !== "success" || !data.items || typeof data.items !== "object") {
      return {};
    }

    const stackEntry = data.items[stackId];
    if (!stackEntry || !stackEntry.plis || typeof stackEntry.plis !== "object") {
      return {};
    }

    const out = {};
    Object.entries(stackEntry.plis).forEach(([plyPn, masse]) => {
      const key = normalizePn(plyPn);
      if (!key) return;
      out[key] = toFloat(masse);
    });

    return out;
  } catch (e) {
    console.error("Erreur fetch masse anhydre", e);
    return {};
  } finally {
    setLoadingMasses(false);
  }
}

  async function handleAddPlies() {
    if (!pliesPn) {
      alert("Merci de sélectionner un StackId.");
      return;
    }

    const current = selectedStacks.reduce(
      (acc, s) => acc + (Number(s.totalPlis) || 0),
      0
    );

    const detailRes = await impFetch(`/api/empilement/${pliesPn}`);
    if (!detailRes.ok) {
      const d = await detailRes.json().catch(() => ({}));
      alert(d.message || "Impossible de récupérer les plis du stack.");
      return;
    }

    const stackData = await detailRes.json().catch(() => ({}));
    const stackRows = Array.isArray(stackData.rows) ? stackData.rows : [];
    const tp = stackRows.length;

    if (tp === 0) {
      alert("Ce stack ne contient aucun pli.");
      return;
    }

    if (current + tp > 30) {
      alert(
        `Impossible d'ajouter : ${current + tp} plis dépasserait la limite de 30.`
      );
      return;
    }

    const addRes = await impFetch("/api/stack/ajout/impregnation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pn: pliesPn }),
    });

    if (!addRes.ok) {
      const d = await addRes.json().catch(() => ({}));
      alert(d.message || "Erreur ajout stack.");
      return;
    }

    const plyList = stackRows
      .map((r) => (r?.plyPn || r?.PlyPN || "").trim())
      .filter(Boolean);

    const anhydreMap = await fetchMasseAnhydreForStacks([pliesPn]);

    let masseConditionnementStack = null;
    let condMap = {};
    let hCondParPli = {};
    let hCondByPos = {};
    let hMerlinParPli = {};
    let hCondStackRef = null;
    let hBancMoyenneRef = null;
    let masseBancParPli = {};
    try {
      const refRes = await impFetch(
        `/api/traitement-thermique/references-completion?stackIds=${encodeURIComponent(pliesPn)}`
      );
      const refData = await refRes.json().catch(() => ({}));
      const refItem = refData?.items?.[pliesPn] || {};
      hCondParPli = refItem.humiditeCondParPli || {};
      hCondByPos = refItem.humiditeCondParPosition || {};
      hMerlinParPli = refItem.humiditeMerlinParPli || {};
      hCondStackRef = refItem.humiditeApresConditionnementStack ?? null;
      hBancMoyenneRef = refItem.humiditeBancMoyenne ?? null;
      const mc = refItem.masseConditionnementStack;
      if (mc != null && Number.isFinite(Number(mc))) {
        masseConditionnementStack = Number(mc);
      }
      masseBancParPli = refItem.masseInitialeParPli || {};
      if (masseConditionnementStack > 0 && Object.keys(masseBancParPli).length) {
        condMap = buildMasseCondPliMapFromBancAndStack(
          plyList,
          masseBancParPli,
          masseConditionnementStack
        );
      }
    } catch (e) {
      console.warn("references-completion / masses cond.", e);
    }

    if (!Object.keys(condMap).length) {
      condMap = await fetchMassesForPlys(plyList, pliesPn);
    }

    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      if (next.length < current + tp) {
        next.push(...makeRows(current + tp - next.length));
      }

      for (let i = 0; i < tp; i++) {
        const pn = (stackRows[i]?.plyPn || stackRows[i]?.PlyPN || "").trim();
        const key = normalizePn(pn);

        const masseConditionnement =
          condMap[key] !== undefined && condMap[key] !== null
            ? String(condMap[key])
            : "";

        const masseAnhydre =
          anhydreMap[key] !== undefined && anhydreMap[key] !== null
            ? String(anhydreMap[key])
            : "";

        const masseApresTraitement = next[current + i]?.masses?.[0] || "";

        const masseInitialeBanc =
          masseBancParPli[key] != null
            ? String(masseBancParPli[key])
            : masseBancParPli[pn] != null
              ? String(masseBancParPli[pn])
              : "";

        const hMerlinPli =
          hMerlinParPli[key] ??
          hMerlinParPli[pn] ??
          hBancMoyenneRef ??
          "";

        let hCondResolved =
          hCondParPli[key] ??
          hCondParPli[normalizePn(pn)] ??
          hCondByPos[String(i + 1)] ??
          hCondByPos[i + 1] ??
          null;

        if (
          hCondResolved == null &&
          hMerlinPli !== "" &&
          hCondStackRef != null &&
          hBancMoyenneRef != null
        ) {
          hCondResolved = calcHumiditeCondPli(
            hMerlinPli,
            hCondStackRef,
            hBancMoyenneRef
          );
        }
        if (
          hCondResolved == null &&
          toFloat(masseConditionnement) > 0 &&
          toFloat(masseInitialeBanc) > 0 &&
          hMerlinPli !== ""
        ) {
          hCondResolved = calcHumiditeCondStack(
            masseConditionnement,
            masseInitialeBanc,
            hMerlinPli
          );
        }

        next[current + i] = {
          ...next[current + i],
          plyPn: pn,
          masseConditionnement,
          masseAnhydre,
          masseInitialeBanc,
          hMerlinPli: hMerlinPli !== "" ? String(hMerlinPli) : "",
          hCondStackRef:
            hCondStackRef != null ? String(hCondStackRef) : "",
          hBancMoyenneRef:
            hBancMoyenneRef != null ? String(hBancMoyenneRef) : "",
          hCondPli:
            hCondResolved != null && Number.isFinite(Number(hCondResolved))
              ? String(hCondResolved)
              : "",
          masses: [masseApresTraitement],
        };
      }

      return next;
    });

    setSelectedStacks((prev) => [
      ...prev,
      {
        stackId: pliesPn,
        totalPlis: tp,
        masseConditionnementStack,
      },
    ]);
    setPliesPn("");
    setPliesSearch("");
    await reloadPlies();
  }

  async function handleRemoveLastPlies() {
    const last = selectedStacks[selectedStacks.length - 1];
    if (!last) return;

    const { stackId, totalPlis: tp } = last;

    const res = await impFetch("/api/stack/supprimer/impregnation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pn: stackId }),
    });

    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d.message || "Erreur suppression stack.");
      return;
    }

    setRows((prev) => {
      const kept = prev.slice(0, prev.length - tp);
      return kept.concat(makeRows(tp));
    });

    setSelectedStacks((prev) => prev.slice(0, -1));
    await reloadPlies();
  }

  function setRowMass(i, massIdx, value) {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[i] };
      const masses = [...ensureMassesArray(row.masses)];
      while (masses.length <= massIdx) masses.push("");
      masses[massIdx] = value.replace(/[^0-9.,]/g, "");
      row.masses = masses;
      next[i] = row;
      return next;
    });
  }

  function setRowField(i, field, value) {
    if (field === "masseConditionnement") {
      setRows((prev) => {
        const next = [...prev];
        const row = { ...next[i], masseConditionnement: value.replace(/[^0-9.,]/g, "") };
        next[i] = row;
        return next;
      });
      return;
    }
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  }

  const maxMassSlots = useMemo(() => {
    const visible = rows.slice(0, totalPlis);
    if (!visible.length) return 1;
    return Math.max(1, ...visible.map((r) => massSlotsToShow(r)));
  }, [rows, totalPlis]);

  const tableGridStyle = useMemo(
    () => ({
      gridTemplateColumns: `60px ${IMP_PLY_ID_COL_PX}px 118px ${"118px 96px ".repeat(
        maxMassSlots
      )}140px`,
      minWidth: `${
        60 + IMP_PLY_ID_COL_PX + 118 + maxMassSlots * 214 + 140
      }px`,
    }),
    [maxMassSlots]
  );

  const totalStack = useMemo(() => {
    const visibleRows = rows.slice(0, totalPlis);

    const totalCondPlis = visibleRows.reduce(
      (sum, r) => sum + toFloat(r.masseConditionnement || 0),
      0
    );
    const totalCondStack = selectedStacks.reduce(
      (sum, s) => sum + toFloat(s.masseConditionnementStack || 0),
      0
    );
    const totalCond = totalCondStack > 0 ? totalCondStack : totalCondPlis;

    const totalImpAuto = visibleRows.reduce(
      (sum, r) => sum + toFloat(resolveFinalMasse(r) || 0),
      0
    );

    const totalAnhydre = visibleRows.reduce(
      (sum, r) => sum + toFloat(r.masseAnhydre || 0),
      0
    );

    const totalImpFinal = useMasseTotaleManuelle
      ? toFloat(masseTotaleImpregnationManuelle || 0)
      : totalImpAuto;

    const gain =
      totalImpFinal > 0 && totalAnhydre > 0
        ? (((totalImpFinal - totalAnhydre) / totalAnhydre) * 100).toFixed(2)
        : totalImpFinal > 0 && totalCond > 0
          ? (((totalImpFinal - totalCond) / totalCond) * 100).toFixed(2)
          : "";

    const masseResineTotale =
  totalImpFinal > 0 && totalCond > 0
    ? totalImpFinal - totalCond
    : 0;

    const hImpPlis = visibleRows
      .map((r) => {
        const m = resolveFinalMasse(r);
        return calcHumiditeImpPli(
          m,
          r.masseConditionnement,
          r.masseAnhydre,
          resolveHCondPliForRow(r)
        );
      })
      .filter((v) => v != null && Number.isFinite(v));

    const hImpStackVal = calcHumiditeImpStack(hImpPlis);
    const humiditeApresImpregnation =
      hImpStackVal != null ? formatHumiditePct(hImpStackVal, 2) ?? "" : "";

    return {
      totalCond,
      totalCondPlis,
      totalCondStack,
      totalAnhydre,
      totalImpAuto,
      totalImpFinal,
      gain,
      humiditeApresImpregnation,
      hImpStackNumeric: hImpStackVal,
    };
  }, [
    rows,
    totalPlis,
    useMasseTotaleManuelle,
    masseTotaleImpregnationManuelle,
    selectedStacks,
  ]);

async function handleSubmit(e) {
  e.preventDefault();
  setError("");
  setSuccess("");

  if (!melangePn)
    return setError("Merci de sélectionner l'identifiant du mélange.");
  if (!temperature)
    return setError("Merci de saisir la température de traitement.");
  if (!dureeTraitement)
    return setError("Merci de saisir la durée de traitement.");
  if (selectedStacks.length === 0)
    return setError("Merci d'ajouter au moins un stack de plis.");

  const n = clampInt(totalPlis, 1, 30);
  const visibleRows = rows.slice(0, n);

  for (const r of visibleRows) {
    if (!r.plyPn) continue;
    if (toFloat(resolveFinalMasse(r)) <= 0) {
      return setError(`Pli ${r.plyPn} : saisir la masse après imprégnation`);
    }
  }

  const payload = {
    operator,
    melangePn,
    temperature: toFloat(temperature),
    dureeTraitement: toFloat(dureeTraitement),
    totalPlis: n,
    stackPns: selectedStacks.map((s) => s.stackId),
    rows: visibleRows.map((r, idx) => {
      const masses = ensureMassesArray(r.masses)
        .map((m) => toFloat(m))
        .filter((m) => m > 0);
      return {
        position: idx + 1,
        masseConditionnement: toFloat(r.masseConditionnement),
        massesApresTraitement: masses,
        masseApresTraitement: toFloat(resolveFinalMasse(r)),
        commentaire: r.commentaire || "",
      };
    }),
    masseTotaleImpregnationAuto: totalStack.totalImpAuto,
    masseTotaleImpregnationFinale: totalStack.totalImpFinal,
    masseTotaleSaisieManuelle: useMasseTotaleManuelle,
    timestamp: new Date().toISOString(),
  };

  doSubmit(payload);
}

  async function doSubmit(payload, { isRetry = false } = {}) {
    try {
      setSending(true);
      setError("");

      const res = await impFetch("/api/impregnation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Erreur serveur");

      clearImpDraft();
      setPendingPayload(null);
      setShowReauth(false);
      setReauthPassword("");

      setMelangePn("");
      setMelangeSearch("");
      setTemperature("");
      setDureeTraitement("");
      setPliesPn("");
      setPliesSearch("");
      setSelectedStacks([]);
      setRows(makeRows(12));
      setMasseTotaleImpregnationManuelle("");
      setUseMasseTotaleManuelle(false);
      const stamp = new Date().toLocaleTimeString("fr-FR");
      setSuccess(
        isRetry
          ? `Données enregistrées avec succès (après reconnexion, ${stamp}).`
          : `Données enregistrées avec succès (${stamp}).`
      );
    } catch (err) {
      if (err instanceof ApiAuthError || err?.name === "ApiAuthError") {
        setPendingPayload(payload);
        setShowReauth(true);
        setReauthError("");
        setError(
          "Session expirée. Reconnectez-vous ci-dessous : votre saisie est conservée."
        );
        return;
      }
      setError(err.message || "Erreur d'envoi.");
    } finally {
      setSending(false);
    }
  }

  async function handleReauthSubmit(e) {
    e.preventDefault();
    setReauthError("");
    const user = reauthUsername.trim();
    const pass = reauthPassword;
    if (!user || !pass) {
      setReauthError("Identifiant et mot de passe requis.");
      return;
    }
    try {
      setReauthBusy(true);
      const data = await apiLogin(user, pass);
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setOperator(data.user?.username || user);
      setReauthPassword("");
      if (pendingPayload) {
        await doSubmit(pendingPayload, { isRetry: true });
      }
    } catch (err) {
      setReauthError(err.message || "Échec de la connexion.");
    } finally {
      setReauthBusy(false);
    }
  }

  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="imp-page">
      <Style />

      <div className="imp-card">
        <div className="imp-header">
          <span>Imprégnation</span>
          <span className="imp-header-sub">Traitement des plis</span>
        </div>

        <div className="imp-nav">
          <button
            type="button"
            className="imp-btn-back"
            onClick={() => navigate("/menu")}
          >
            {ui("Retour menu")}
          </button>

          <div className="imp-clock">
            <div className="imp-clock-time">{timeStr}</div>
            <div className="imp-clock-date">{dateStr}</div>
          </div>
        </div>

        <div className="imp-operator">
          <span className="imp-operator-dot" />
          {operator ? operator.toUpperCase() : "OPERATEUR NON CONNECTÉ"}
        </div>

        <form onSubmit={handleSubmit} className="imp-form">
          <div className="imp-section">
            <div className="imp-section-label imp-label-yellow">
              Identifiant du mélange
            </div>

            <div ref={melangeBoxRef} style={{ position: "relative" }}>
              <div className="imp-select-row">
                <input
                  value={melangeSearch}
                  onChange={(e) => {
                    setMelangeSearch(e.target.value);
                    setShowMelangeList(true);
                  }}
                  onFocus={() => setShowMelangeList(true)}
                  placeholder="Sélectionner un PN mélange"
                  className="imp-select-input"
                />
                <button
                  type="button"
                  className="imp-scan-btn"
                  onClick={() => setShowMelangeScanner(true)}
                  title="Scanner QR"
                >
                  📷
                </button>
              </div>

              {melangePn && (
                <div className="imp-selected-badge">
                  <span>PN sélectionné :</span>
                  <strong>{melangePn}</strong>
                  <button
                    type="button"
                    className="imp-clear-btn"
                    onClick={() => {
                      setMelangePn("");
                      setMelangeSearch("");
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {showMelangeList && filteredMelange.length > 0 && (
                <div
                  className="imp-dropdown"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {filteredMelange.map((p) => (
                    <div
                      key={p}
                      className="imp-dropdown-item"
                      onClick={() => {
                        setMelangePn(p);
                        setMelangeSearch(p);
                        setShowMelangeList(false);
                      }}
                    >
                      {p}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="imp-grid-2">
            <div className="imp-section">
              <div className="imp-section-label imp-label-yellow">
                Température de traitement
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={temperature}
                onChange={(e) =>
                  setTemperature(e.target.value.replace(/[^0-9.,]/g, ""))
                }
                className="imp-num-input"
                placeholder="Ex : 180.5"
              />
            </div>

            <div className="imp-section">
              <div className="imp-section-label imp-label-yellow">
                Durée de traitement
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={dureeTraitement}
                onChange={(e) =>
                  setDureeTraitement(e.target.value.replace(/[^0-9.,]/g, ""))
                }
                className="imp-num-input"
                placeholder="Ex : 45"
              />
            </div>
          </div>

          <div className="imp-section">
            <div className="imp-section-label imp-label-gray">
              Identifiant Plies liste
            </div>

            <div ref={pliesBoxRef} style={{ position: "relative" }}>
              <div className="imp-select-row">
                <input
                  value={pliesSearch}
                  onChange={(e) => {
                    setPliesSearch(e.target.value);
                    setShowPliesList(true);
                  }}
                  onFocus={() => setShowPliesList(true)}
                  placeholder="Sélectionner un StackId"
                  className="imp-select-input"
                />
                <button
                  type="button"
                  className="imp-scan-btn"
                  onClick={() => setShowPliesScanner(true)}
                  title="Scanner QR"
                >
                  📷
                </button>

                <button
                  type="button"
                  className="imp-btn-action"
                  onClick={handleAddPlies}
                >
                  {ui("Ajouter")}
                </button>

                <button
                  type="button"
                  className="imp-btn-action imp-btn-danger"
                  onClick={handleRemoveLastPlies}
                >
                  {ui("Supprimer")}
                </button>
              </div>

              {selectedStacks.length > 0 && (
                <div className="imp-stacks-summary">
                  {selectedStacks.map((s, i) => (
                    <span key={`${s.stackId}-${i}`} className="imp-stack-tag">
                      {s.stackId} <em>{s.totalPlis} plis</em>
                    </span>
                  ))}
                </div>
              )}

              {showPliesList && filteredPlies.length > 0 && (
                <div
                  className="imp-dropdown"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {filteredPlies.map((p) => (
                    <div
                      key={p}
                      className="imp-dropdown-item"
                      onClick={() => {
                        setPliesPn(p);
                        setPliesSearch(p);
                        setShowPliesList(false);
                      }}
                    >
                      {p}{" "}
                      {pliesMeta[p] ? `(${pliesMeta[p]} pli${pliesMeta[p] > 1 ? "s" : ""})` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="imp-section imp-section-table">
            <div className="imp-section-label">
              Tableau des plis ({totalPlis} pli{totalPlis > 1 ? "s" : ""})
            </div>

            {(loadingMasses || loadingAnhydre) && (
              <div className="imp-inline-info">
                Chargement des masses de conditionnement / anhydres...
              </div>
            )}

            <div
              className="imp-table"
              style={{ "--imp-ply-id-w": `${IMP_PLY_ID_COL_PX}px` }}
            >
              <div className="imp-table-head imp-table-head-dynamic" style={tableGridStyle}>
                <div className="imp-th imp-th-pos">Pos</div>
                <div className="imp-th imp-th-ply">Identifiant du pli</div>
                <div className="imp-th">Masse cond.</div>
                {Array.from({ length: maxMassSlots }, (_, i) => (
                  <React.Fragment key={`mass-head-${i}`}>
                    <div className="imp-th">Masse {i + 1}</div>
                    <div className="imp-th">Gain {i + 1}</div>
                  </React.Fragment>
                ))}
                <div className="imp-th">Humidité après imprég.</div>
              </div>

              {rows.slice(0, totalPlis).map((r, idx) => {
                const masses = ensureMassesArray(r.masses);
                const slots = massSlotsToShow(r);
                const finalMasse = resolveFinalMasse(r);
                const humidite = computeHumiditeApresImpregnation(
                  finalMasse,
                  r.masseConditionnement,
                  r.masseAnhydre,
                  r
                );

                return (
                <div
                  key={idx}
                  className={`imp-table-row imp-table-row-dynamic ${
                    idx % 2 === 0 ? "imp-row-even" : "imp-row-odd"
                  }`}
                  style={tableGridStyle}
                >
                  <div className="imp-td imp-td-pos">{idx + 1}</div>

                  <div className="imp-td imp-td-ply">
                    <div
                      className="imp-ply-id"
                      title={r.plyPn || undefined}
                    >
                      {r.plyPn || "—"}
                    </div>
                  </div>

                  <div className="imp-td">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.masseConditionnement ?? ""}
                      onChange={(e) =>
                        setRowField(idx, "masseConditionnement", e.target.value)
                      }
                      className="imp-cell-input"
                      placeholder="0.00"
                    />
                  </div>

                  {Array.from({ length: maxMassSlots }, (_, massIdx) => {
                    const gain = computeGainMassique(
                      masses[massIdx] || "",
                      r.masseAnhydre
                    );
                    const isActive = massIdx < slots;

                    return (
                      <React.Fragment key={`mass-${idx}-${massIdx}`}>
                        <div className="imp-td">
                          {isActive ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              value={masses[massIdx] ?? ""}
                              onChange={(e) =>
                                setRowMass(idx, massIdx, e.target.value)
                              }
                              className="imp-cell-input"
                              placeholder="0.00"
                            />
                          ) : (
                            <span className="imp-cell-muted">—</span>
                          )}
                        </div>
                        <div className="imp-td">
                          {isActive ? (
                            <div className={`imp-gain-badge ${gainBadgeClass(gain)}`}>
                              {gain ? `${gain} %` : "--"}
                            </div>
                          ) : (
                            <span className="imp-cell-muted">—</span>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  <div className="imp-td">
                    <div className="imp-gain-badge imp-gain-neutral">
                      {humidite
                        ? `${humidite} %`
                        : toFloat(finalMasse) > 0
                          ? "—"
                          : ""}
                    </div>
                  </div>
                </div>
                );
              })}

              <div className="imp-total-block">
                <div className="imp-total-header">
                  <span className="imp-section-label">
                    Masse totale après imprégnation
                  </span>
                  <label className="imp-total-toggle">
                    <input
                      type="checkbox"
                      checked={useMasseTotaleManuelle}
                      onChange={(e) =>
                        setUseMasseTotaleManuelle(e.target.checked)
                      }
                    />
                    <span>Saisie manuelle opérateur</span>
                  </label>
                </div>

                <div className="imp-total-grid">
                  <div className="imp-total-col">
                    <div className="imp-total-mini-label">Calcul auto</div>
                    <input
                      value={totalStack.totalImpAuto.toFixed(2)}
                      readOnly
                      className="imp-cell-input imp-cell-readonly"
                    />
                  </div>

                  <div className="imp-total-col">
                    <div className="imp-total-mini-label">Valeur retenue</div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={
                        useMasseTotaleManuelle
                          ? masseTotaleImpregnationManuelle
                          : totalStack.totalImpAuto.toFixed(2)
                      }
                      onChange={(e) =>
                        setMasseTotaleImpregnationManuelle(
                          e.target.value.replace(/[^0-9.,]/g, "")
                        )
                      }
                      disabled={!useMasseTotaleManuelle}
                      className={`imp-cell-input ${
                        !useMasseTotaleManuelle ? "imp-cell-readonly" : ""
                      }`}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="imp-total-col">
                    <div className="imp-total-mini-label">
                      Humidité globale après imprég.
                    </div>
                    <div className="imp-gain-badge imp-gain-neutral">
                      {totalStack.humiditeApresImpregnation
                        ? `${totalStack.humiditeApresImpregnation} %`
                        : ""}
                    </div>
                  </div>
                </div>
              </div>

              <div className="imp-table-row imp-table-row-dynamic imp-total-row" style={tableGridStyle}>
                <div className="imp-td imp-td-pos"></div>

                <div className="imp-td imp-td-ply">
                  <input
                    value="TOTAL STACK"
                    readOnly
                    className="imp-cell-input imp-cell-readonly imp-total-input"
                  />
                </div>

                <div className="imp-td">
                  <input
                    value={totalStack.totalCond.toFixed(2)}
                    readOnly
                    className="imp-cell-input imp-cell-readonly imp-total-input"
                  />
                </div>

                {Array.from({ length: maxMassSlots }, (_, massIdx) => (
                  <React.Fragment key={`total-mass-${massIdx}`}>
                    <div className="imp-td">
                      {massIdx === 0 ? (
                        <input
                          value={
                            totalStack.totalImpFinal > 0
                              ? totalStack.totalImpFinal.toFixed(2)
                              : ""
                          }
                          readOnly
                          className="imp-cell-input imp-cell-readonly imp-total-input"
                          placeholder="0.00"
                        />
                      ) : (
                        <span className="imp-cell-muted">—</span>
                      )}
                    </div>
                    <div className="imp-td">
                      {massIdx === 0 ? (
                        <div className="imp-gain-badge imp-gain-neutral">
                          {totalStack.gain ? `${totalStack.gain} %` : ""}
                        </div>
                      ) : (
                        <span className="imp-cell-muted">—</span>
                      )}
                    </div>
                  </React.Fragment>
                ))}

                <div className="imp-td">
                  <div className="imp-gain-badge imp-gain-neutral">
                    {totalStack.humiditeApresImpregnation
                      ? `${totalStack.humiditeApresImpregnation} %`
                      : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="imp-msg imp-msg-error">{error}</div>}
          {success && <div className="imp-msg imp-msg-success">{success}</div>}

          <div className="imp-submit-row">
            <button type="submit" disabled={sending} className="imp-btn-submit">
              {sending ? "Envoi en cours..." : "OK confirmation et envoi"}
            </button>

            <button type="button" className="imp-btn-time">
              Time
              <div className="imp-btn-time-sub">{timeStr}</div>
            </button>
          </div>
        </form>
      </div>

      {showMelangeScanner && (
        <div
          className="imp-modal-backdrop"
          onClick={() => setShowMelangeScanner(false)}
        >
          <div className="imp-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="imp-modal-title">Scanner mélange</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner
                onScan={(r) => {
                  const pn = r?.[0]?.rawValue?.trim();
                  if (!pn) return;
                  setMelangePn(pn);
                  setMelangeSearch(pn);
                  setShowMelangeList(false);
                  setShowMelangeScanner(false);
                }}
                onError={console.error}
                constraints={{ facingMode: "environment" }}
              />
            </div>
            <button
              type="button"
              className="imp-btn-close-scanner"
              onClick={() => setShowMelangeScanner(false)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {showReauth && (
        <div className="imp-modal-backdrop">
          <div
            className="imp-modal-confirm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="imp-reauth-title"
          >
            <div className="imp-confirm-header">
              <span id="imp-reauth-title">Session expirée</span>
            </div>
            <form className="imp-confirm-body" onSubmit={handleReauthSubmit}>
              <p className="imp-confirm-msg">
                Reconnectez-vous pour enregistrer sans perdre les masses saisies.
              </p>
              {reauthError && (
                <p className="imp-confirm-msg" style={{ color: "#ff8080" }}>
                  {reauthError}
                </p>
              )}
              <input
                className="imp-select-input"
                value={reauthUsername}
                onChange={(e) => setReauthUsername(e.target.value)}
                placeholder="Identifiant"
                autoComplete="username"
                autoFocus
              />
              <input
                className="imp-select-input"
                type="password"
                value={reauthPassword}
                onChange={(e) => setReauthPassword(e.target.value)}
                placeholder="Mot de passe"
                autoComplete="current-password"
              />
              <div className="imp-confirm-footer">
                <button
                  type="button"
                  className="imp-btn-back"
                  onClick={() => {
                    setShowReauth(false);
                    setReauthPassword("");
                  }}
                >
                  Plus tard
                </button>
                <button
                  type="submit"
                  className="imp-btn-submit"
                  disabled={reauthBusy || sending}
                >
                  {reauthBusy || sending
                    ? "Connexion…"
                    : "Se reconnecter et enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPliesScanner && (
        <div
          className="imp-modal-backdrop"
          onClick={() => setShowPliesScanner(false)}
        >
          <div className="imp-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="imp-modal-title">Scanner plies</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner
                onScan={(r) => {
                  const pn = r?.[0]?.rawValue?.trim();
                  if (!pn) return;
                  setPliesPn(pn);
                  setPliesSearch(pn);
                  setShowPliesList(false);
                  setShowPliesScanner(false);
                }}
                onError={console.error}
                constraints={{ facingMode: "environment" }}
              />
            </div>
            <button
              type="button"
              className="imp-btn-close-scanner"
              onClick={() => setShowPliesScanner(false)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .imp-page {
        min-height: 100vh;
        width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: 16px;
        font-family: Inter, Segoe UI, sans-serif;
      }

      .imp-card {
        width: 100%;
        max-width: 1280px;
        background: #011e2b;
        border: 1px solid #1a4a5a;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px #0a3040;
      }

      .imp-header {
        background: linear-gradient(135deg, #E6FF3E, #c8e800);
        color: #000;
        padding: 14px 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }

      .imp-header span {
        font-size: 15px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.1em;
      }

      .imp-header-sub {
        font-size: 11px;
        font-weight: 500;
        opacity: 0.6;
      }

      .imp-nav {
        background: #010f14;
        padding: 8px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #1a4a5a;
      }

      .imp-btn-back {
        background: transparent;
        color: #7ab8cc;
        border: 1px solid #1a4a5a;
        border-radius: 6px;
        padding: 5px 10px;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
      }

      .imp-clock { text-align: right; }
      .imp-clock-time {
        color: #E6FF3E;
        font-size: 15px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .imp-clock-date { color: #5a8a99; font-size: 10px; }

      .imp-operator {
        display: flex;
        align-items: center;
        gap: 10px;
        background: linear-gradient(90deg, #012836, #013d50);
        border: 1px solid #1a5a6a;
        border-radius: 10px;
        margin: 14px 20px 0;
        padding: 12px 16px;
        color: #fff;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.06em;
      }

      .imp-operator-dot {
        width: 10px;
        height: 10px;
        background: #00B050;
        border-radius: 50%;
        box-shadow: 0 0 6px #00B050;
        flex-shrink: 0;
      }

      .imp-form {
        padding: 16px 20px 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .imp-section {
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 12px;
        padding: 14px 16px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .imp-section-table { padding: 14px 12px; }

      .imp-section-label {
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #7ab8cc;
      }

      .imp-label-yellow { color: #FFD966; }
      .imp-label-gray { color: #c8c8d8; }

      .imp-grid-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }

      .imp-select-row {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
      }

      .imp-select-input {
        flex: 1;
        min-width: 140px;
        background: #003818;
        color: #7aff9a;
        border: 1px solid #005020;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 14px;
        font-weight: 600;
        outline: none;
      }

      .imp-scan-btn {
        width: 46px;
        height: 46px;
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        font-size: 20px;
        cursor: pointer;
        flex-shrink: 0;
      }

      .imp-btn-action {
        background: #F2F2F2;
        color: #000;
        border: 2px solid #C00000;
        border-radius: 8px;
        padding: 8px 12px;
        font-weight: 700;
        font-size: 12px;
        cursor: pointer;
        white-space: nowrap;
        height: 46px;
      }

      .imp-btn-danger { background: #fff0f0; }

      .imp-selected-badge {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 6px;
        background: #002810;
        border: 1px solid #00B050;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: 12px;
        color: #7aff9a;
      }

      .imp-selected-badge strong { flex: 1; font-size: 13px; }

      .imp-clear-btn {
        background: transparent;
        border: none;
        color: #ff8080;
        cursor: pointer;
        font-size: 14px;
        font-weight: 700;
      }

      .imp-stacks-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
      }

      .imp-stack-tag {
        background: #012836;
        border: 1px solid #E6FF3E;
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 11px;
        color: #E6FF3E;
        font-weight: 600;
      }

      .imp-stack-tag em {
        color: #7ab8cc;
        font-style: normal;
        margin-left: 4px;
      }

      .imp-dropdown {
        position: absolute;
        left: 0;
        right: 0;
        margin-top: 4px;
        max-height: 200px;
        overflow-y: auto;
        background: #002818;
        border: 1px solid #005020;
        border-radius: 8px;
        z-index: 50;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }

      .imp-dropdown-item {
        padding: 8px 14px;
        color: #aaffcc;
        font-size: 13px;
        cursor: pointer;
        border-bottom: 1px solid #003818;
      }

      .imp-num-input {
        width: 100%;
        background: #010F14;
        color: #fff;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 18px;
        font-weight: 700;
        text-align: center;
        outline: none;
      }

      .imp-inline-info {
        background: #0a2430;
        border: 1px solid #1a4a5a;
        color: #7ab8cc;
        font-size: 12px;
        font-weight: 600;
        border-radius: 8px;
        padding: 8px 12px;
      }

      .imp-table {
        border: 1px solid #1a4a5a;
        border-radius: 10px;
        overflow: auto;
      }

      .imp-table-head-dynamic,
      .imp-table-row-dynamic {
        display: grid;
        width: max-content;
      }

      .imp-cell-muted {
        color: #4a5568;
        font-size: 12px;
      }

      .imp-table-head-7,
      .imp-table-row-7 {
        display: grid;
        grid-template-columns: 60px 2fr 140px 140px 140px 160px 1.3fr;
        min-width: 1100px;
      }

      .imp-table-head {
        background: linear-gradient(90deg, #012a3d, #013d50);
        border-bottom: 1px solid #1a4a5a;
      }

      .imp-row-even { background: #011820; }
      .imp-row-odd { background: #012030; }

      .imp-table-row:not(:last-child) {
        border-bottom: 1px solid #1a3a4a;
      }

      .imp-th {
        padding: 10px 6px;
        text-align: center;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #7ab8cc;
        border-right: 1px solid #1a4a5a;
      }

      .imp-th:last-child { border-right: none; }

      .imp-td {
        padding: 5px 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-right: 1px solid #1a3a4a;
      }

      .imp-td:last-child { border-right: none; }

      .imp-td-pos {
        color: #7ab8cc;
        font-size: 13px;
        font-weight: 700;
      }

      .imp-th-pos,
      .imp-td-pos {
        position: sticky;
        left: 0;
        z-index: 4;
        background: linear-gradient(90deg, #012a3d, #013d50);
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.35);
      }

      .imp-table-row-dynamic .imp-td-pos {
        background: inherit;
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.25);
      }

      .imp-row-even .imp-td-pos { background: #011820; }
      .imp-row-odd .imp-td-pos { background: #012030; }
      .imp-total-row .imp-td-pos { background: #012836; }

      .imp-th-ply,
      .imp-td-ply {
        position: sticky;
        left: 60px;
        z-index: 3;
        justify-content: stretch;
        align-items: stretch;
        width: var(--imp-ply-id-w, 420px);
        min-width: var(--imp-ply-id-w, 420px);
        max-width: var(--imp-ply-id-w, 420px);
        box-sizing: border-box;
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.2);
      }

      .imp-th-ply {
        background: linear-gradient(90deg, #012a3d, #013d50);
        white-space: normal;
        line-height: 1.25;
        padding-left: 8px;
        padding-right: 8px;
      }

      .imp-table-row-dynamic .imp-td-ply {
        background: inherit;
      }

      .imp-row-even .imp-td-ply { background: #011820; }
      .imp-row-odd .imp-td-ply { background: #012030; }
      .imp-total-row .imp-td-ply { background: #012836; }

      .imp-ply-id {
        display: block;
        width: 100%;
        min-width: 100%;
        box-sizing: border-box;
        text-align: left;
        padding: 8px 10px;
        font-size: 13px;
        font-weight: 600;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        letter-spacing: 0.03em;
        line-height: 1.35;
        white-space: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        background: #010f14;
        color: #7aff9a;
        border: 1px dashed #1a4a5a;
        border-radius: 6px;
        -webkit-overflow-scrolling: touch;
      }

      .imp-cell-input {
        width: 98%;
        background: #010f14;
        color: #fff;
        border: 1px solid #1a4a5a;
        border-radius: 6px;
        padding: 6px 5px;
        font-size: 11px;
        outline: none;
        text-align: center;
      }

      .imp-cell-readonly {
        color: #7aff9a;
        cursor: not-allowed;
        border-style: dashed;
        font-size: 10px;
      }

      .imp-gain-badge {
        min-width: 92px;
        text-align: center;
        padding: 8px 10px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
        border: 1px solid transparent;
      }

      .imp-gain-neutral {
        background: #10202a;
        color: #7ab8cc;
        border-color: #1a4a5a;
      }

      .imp-total-block {
        margin-top: 12px;
        padding: 8px 10px 10px;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.25);
      }

      .imp-total-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
      }

      .imp-total-toggle {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #fff;
      }

      .imp-total-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
      }

      .imp-total-col {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .imp-total-mini-label {
        font-size: 11px;
        text-transform: uppercase;
        opacity: 0.7;
        color: #c8c8d8;
      }

      .imp-total-row {
        background: linear-gradient(90deg, #1d2a00, #243600);
        border-top: 2px solid #E6FF3E;
      }

      .imp-total-input {
        color: #E6FF3E !important;
        font-weight: 800;
        border-color: #8ea800 !important;
        background: #162000 !important;
      }

      .imp-msg {
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 13px;
        font-weight: 600;
      }

      .imp-msg-error {
        background: #2a0808;
        border: 1px solid #C00000;
        color: #ff9090;
      }
      
      .imp-gain-alert {
  background: #2a0808;
  color: #ff4040;
  border-color: #C00000;
  animation: imp-pulse-alert 1s infinite;
}

.imp-gain-ok {
  background: #002810;
  color: #00B050;
  border-color: #00B050;
}

.imp-gain-warn {
  background: #2a2008;
  color: #fbbf24;
  border-color: #d97706;
}

@keyframes imp-pulse-alert {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

      .imp-msg-success {
        background: #002810;
        border: 1px solid #00B050;
        color: #80ff90;
      }

      .imp-submit-row {
        display: flex;
        gap: 10px;
        align-items: center;
      }

      .imp-btn-submit {
        flex: 1;
        background: linear-gradient(135deg, #00B050, #008a3c);
        color: #000;
        border: none;
        border-radius: 10px;
        padding: 14px;
        font-size: 15px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        cursor: pointer;
      }

      .imp-btn-submit:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .imp-btn-time {
        min-width: 110px;
        background: #C00000;
        color: #fff;
        border: 2px solid #7F0000;
        border-radius: 8px;
        padding: 8px 10px;
        font-weight: 700;
        font-size: 12px;
        cursor: default;
        text-align: center;
      }

      .imp-btn-time-sub {
        font-size: 11px;
        margin-top: 2px;
        font-variant-numeric: tabular-nums;
      }

      .imp-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 999;
        backdrop-filter: blur(4px);
      }

      .imp-modal-scanner {
        width: 90%;
        max-width: 420px;
        background: #011e2b;
        border: 1px solid #00B050;
        border-radius: 14px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        box-shadow: 0 0 40px rgba(0,176,80,0.2);
      }

      .imp-modal-title {
        color: #fff;
        font-size: 14px;
        font-weight: 700;
        text-align: center;
      }

      .imp-btn-close-scanner {
        background: #C00000;
        color: #fff;
        border: none;
        border-radius: 8px;
        padding: 10px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
      }
      .imp-modal-confirm {
  width: 100%;
  max-width: 480px;
  background: #011e2b;
  border: 2px solid #C00000;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 50px rgba(192, 0, 0, 0.3);
  overflow: hidden;
}

.imp-confirm-header {
  background: linear-gradient(135deg, #C00000, #800000);
  color: #fff;
  padding: 14px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.imp-confirm-body {
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.imp-confirm-msg {
  color: #7ab8cc;
  font-size: 13px;
  margin: 0;
}

.imp-confirm-plis {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.imp-confirm-pli-tag {
  background: #2a0808;
  color: #ff4040;
  border: 1px solid #C00000;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
}

.imp-confirm-question {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  padding-top: 6px;
  border-top: 1px solid #1a4a5a;
}

.imp-confirm-footer {
  display: flex;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid #1a4a5a;
}

.imp-confirm-btn-cancel {
  flex: 1;
  height: 42px;
  background: #C00000;
  color: #fff;
  border: 2px solid #800000;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}

.imp-confirm-btn-ok {
  flex: 1;
  height: 42px;
  background: linear-gradient(135deg, #00B050, #008a3c);
  color: #000;
  border: none;
  border-radius: 8px;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
}

      @media (max-width: 767px) {
        .imp-grid-2 { grid-template-columns: 1fr; }
        .imp-card { max-width: 100%; }
      }

      @media (min-width: 1024px) {
        .imp-form { padding: 20px 36px 36px; gap: 22px; }
        .imp-th { font-size: 11px; padding: 12px 8px; }
        .imp-cell-input { font-size: 12px; padding: 8px 6px; }
        .imp-num-input { font-size: 26px; }
        .imp-btn-submit { font-size: 18px; padding: 18px; }
        .imp-gain-badge { font-size: 14px; }
      }
    `}</style>
  );
}